import { Config } from 'config';

import { forEach, forEachRight, filter, find,
         every, reduce, invokeMap, compact } from 'lodash';

import { flatten, isArray, resolve, map$, invokeMap$ } from 'gear';

import { Xml } from '../util';

import { Point, Matrix, Bounds } from '../geometry';

import { Color } from '../style';
import { GroupProcessor, updateArray$ } from './detail/processor';

export const Figures = {};

Figures.toXmlString = function( figures ) {
  const xml  = Xml.fromString('<Cdl></Cdl>');
  forEach( figures, figure => {
    figure.buildXml(xml.documentElement);
  });
  return Xml.toString(xml);
};

Figures.lock = function( figures ) {
  //invokeMap(figures,'lock');
  for(let k=0,kEnd=figures.length;k<kEnd;++k) {
    figures[k].lock();
  }
  return figures;
};

Figures.invalidated = function( figures ) {
  // return some( figures, f => f.invalidated() );
  for(let k=0,kEnd=figures.length;k<kEnd;++k) {
    if( figures[k].invalidated() ) {
      return true;
    }
  }
  return false;
};

Figures.currentlyProcessing = function( figures ) {
  // return some( figures, f => f.currentlyProcessing() );
  for(let k=0,kEnd=figures.length;k<kEnd;++k) {
    if( figures[k].currentlyProcessing() ) {
      return true;
    }
  }
  return false;
};

const computeBounds_ = function( figures, boundsType ) {
  if( ! isArray(figures) ) {
    return figures[boundsType]();
  }
  return Bounds.union(invokeMap(figures,boundsType));
};

Figures.vectorBounds_ = function( figures ) {
  return computeBounds_(figures,'vectorBounds_');
};

Figures.bounds_ = function( figures ) {
  return computeBounds_(figures,'bounds_');
};

Figures.materialBounds_ = function(figures) {
  return computeBounds_(figures,'materialBounds_');
};

// Review, to be used with sync bounded things ( Cx.bounds(polyregions) ),
// this shouldn't be in Figures
Figures.bounds = function(things) {
  return computeBounds_(things,'bounds');
};

Figures.globalPreVectorBounds_ = function( figures ) {
  return computeBounds_(figures,'globalPreVectorBounds_');
};

Figures.globalPreBounds_ = function( figures ) {
  return computeBounds_(figures,'globalPreBounds_');
};

Figures.globalVectorBounds_ = function( figures ) {
  return computeBounds_(figures,'globalVectorBounds_');
};

Figures.globalBounds_ = function( figures ) {
  return computeBounds_(figures,'globalBounds_');
};

Figures.globalMaterialBounds_ = function( figures ) {
  return computeBounds_(figures,'globalMaterialBounds_');
};

const computeBounds$ = function( figures, boundsType ) {
  if( ! isArray(figures) ) {
    return figures[boundsType]();
  }
  return updateArray$(figures,'update') .then( () => { // Force batch update$
  return invokeMap$(figures,boundsType) .then( values => {
  return Bounds.union( compact(values) );
  });});
};

Figures.vectorBounds$ = function( figures ) {
  return computeBounds$(figures,'vectorBounds$');
};

Figures.bounds$ = function( figures,v,config ) {
  if( v === undefined ) {
    return computeBounds$(figures,'bounds$');
  }
  else {
    return Figures.fit$(figures,v,config);
  }
};

Figures.globalVectorBounds$ = function( figures ) {
  return computeBounds$(figures,'globalVectorBounds$');
};

Figures.globalBounds$ = function( figures ) {
  return computeBounds$(figures,'globalBounds$');
};

Figures.materialBounds$ = function(figures) {
  return computeBounds$(figures,'materialBounds$');
};

Figures.globalMaterialBounds$ = function(figures) {
  return computeBounds$(figures,'globalMaterialBounds$');
};


Figures._computeSizingScale = function(width,height,options) {
  const locked = options.locked === undefined ? true : options.locked;
  const fit = options.fit || 'Min' ;
  const ow = options.width  ? ( options.width  * ( options.scale || 1.0 ) ) : undefined;
  const oh = options.height ? ( options.height * ( options.scale || 1.0 ) ) : undefined;
  const margin = options.margin || 0;
  const rm = options.relativeMargin;

  let mw = margin, mh = margin;
  if( rm ) {
    if( ow ) { mw = ow *rm; }
    if( oh ) { mh = oh *rm; }
  }
  let sX = ow ? (ow-2*mw) / width  : 1.0,
      sY = oh ? (oh-2*mh) / height : 1.0;
  if(locked) {
    if( ow && oh ) {
      switch(fit) {
        case 'Min'  : if( sY > sX ) { sY = sX; } else { sX = sY; } break;
        case 'Max'  : if( sY < sX ) { sY = sX; } else { sX = sY; } break;
        case 'Mean' : const sM = Math.sqrt(Math.abs(sX*sY)); sX=sY=sM; break;
      }
    }
    else {
      if( ow ) { sY = sX; } else { sX = sY; }
    }
  }
  return { sX: sX, sY: sY };
};

// options :
// {
//   width, height,
//   locked: true,
//   fit: ['Max'/'Min'/'Mean'],
//   pin: boundsCenter,
//   material: false
// }
// Example : Set the width of a figure preserving aspect ratio
// Figures.setSize$(view.selectedFigures,{width:400})
Figures.setSize$ = function( figures, options, iteration = 0 ) { // [CHECK][D]
  if( ! isArray( figures ) ) {
    figures = [ figures ];
  }

  return Figures[ options.boundsType === 'material' ? 'materialBounds$' : 'bounds$' ](figures) .then( bounds => {

  let w = bounds.width, h = bounds.height;
  if( options.orientation === 'Rotated' ) { const d = w; w = h; h = d; }
  let s = Figures._computeSizingScale(w,h,options);
  if( options.orientation === 'Any' ) {
    const sr = Figures._computeSizingScale(h,w,options);
    if( sr.sX * sr.sY > s.sX * s.sY ) {
      s = sr;
    }
  }
  const pin = options.pin || bounds.center;
  //_.forEach(figures, function(f) {
  for(let k=0,kEnd=figures.length;k<kEnd;++k) {
    figures[k].scaleFrom(pin, s.sX, s.sY);
  }//);

  return figures;
  }).then( figures => {
    return Figures[ options.boundsType === 'material' ? 'materialBounds$' : 'bounds$' ](figures);
  }).then( bounds=>{
    let w = bounds.width, h = bounds.height;
    if( options.orientation === 'Rotated' ) { const d = w; w = h; h = d; }
    let s = Figures._computeSizingScale(w,h,options);
    if( options.orientation === 'Any' ) {
      const sr = Figures._computeSizingScale(h,w,options);
      if( sr.sX * sr.sY > s.sX * s.sY ) {
        s = sr;
      }
    }
    if (Math.abs(1.0-s.sX)<0.00001 && Math.abs(1-s.sY)<0.00001) {
      return figures;
    }
    else
    {
      if (iteration > 10)//avoid an infinite loop in case we dont converge ( from testing we converge in 3 iterations when pens are present )
      {
        return figures;
      }
      else
      {
        return Figures.setSize$(figures, options, iteration+1);
      }
    }
    });
};

const _fit$ = function( figures, toBounds, config = {} ) {
  if( ! config.ignoreWidth  ) {
    config.width = toBounds.width;
  }
  if( ! config.ignoreHeight ) {
    config.height = toBounds.height;
  }
  return Figures.center$(figures,toBounds) .then( () => {
  return Figures.setSize$(figures, config);
  });
};

Figures.fit$ = function( figures, to, config ) {
  if( ! isArray(figures) ) {
    figures = [figures];
  }
  const toBounds = to.bounds || to.lx !== undefined ? to : null ;
  if( toBounds ) {
    return _fit$(figures,toBounds,config);
  }
  else {
    return Figures.bounds$(to) .then( toBounds => {
    return _fit$(figures,toBounds,config);
    });
  }
};

Figures.fastTranslate_ = function( figures, offset ) {
  return invokeMap(figures,'fastTranslate', offset);
};
Figures.fastTranslate = function( figures, x, y, zoom ) {
  let offset;
  if(x.x!==undefined) {
    offset = x; zoom = y;
  }
  else {
    offset = new Point(x,y);
  }
  if( zoom === undefined ) {
    return Figures.translate(figures,offset);
  }
  offset = Point.asFastOffset(offset,zoom);

  return Figures.fastTranslate_(figures,offset);
};

Figures.translate = function( figures, x, y) { if(y===undefined) { y = x.y; x = x.x; }
  return invokeMap(figures,'translate', x, y);
};

Figures.transform = function( figures, matrix) {
  return invokeMap(figures,'transform',matrix);
};

Figures.scaleFrom = function( figures, point, sx, sy, deg) {
  return invokeMap(figures,'scaleFrom', point, sx, sy, deg );
};

Figures.rotateAround = function( figures, point, angle ) {
  return invokeMap(figures,'rotateAround', point, angle );
};

Figures.shearAround = function( figures, point, sx, sy, deg) {
  return invokeMap(figures, 'shearAround', point, sx, sy, deg );
};

const _center$ = function( figures, point, zoom ) {
  return Figures.bounds$( figures ).then( bounds => {
    const offset = point.minus( bounds.center );
    Figures.fastTranslate(figures,offset,zoom);
    return figures;
  });
};

// [TODO][m]
Figures.center$ = function(things, to, y) { if( y !== undefined ) { to = new Point(to,y); }
  if( to === undefined ) {
    if( things.center ) {
      return resolve( things.center );
    }
    return Figures.bounds$(things).then( bounds => { return bounds.center; });
  }
  else {
    if( to.zoom ) {
      // Optimization, if we can get the zoom, we can fastTranslate the figures
      return _center$(things,to.center,to.zoom);
    }
    const point = to.x !== undefined ? to : to.center ? to.center : null;
    if( point ) {
      return _center$(things,point);
    }
    else {
      return Figures.center$(to) .then( point => _center$(things,point) );
    }
  }
};

Figures.rotate$ = function( figures, angle ) {
  return Figures.bounds$(figures).then( bounds => {
    const pivot = bounds.center;
    //_.forEach(figures,function(f) {
    for(let k=0,kEnd=figures.length;k<kEnd;++k) {
      figures[k].rotateAround(pivot,angle);
    }//);
    return figures;
  });
};

Figures.scale$ = function( figures, sx, sy) {
  return Figures.bounds$(figures).then( bounds => {
    const pivot = bounds.center;
    //_.forEach(figures,function(f) {
    for(let k=0,kEnd=figures.length;k<kEnd;++k) {
      figures[k].scaleFrom(pivot,sx,sy);
    }//);
    return figures;
  });
};

Figures.mirrorX$ = function( figures ) {
  return Figures.scale$(figures,-1,1);
};

Figures.mirrorY$ = function( figures ) {
  return Figures.scale$(figures,1,-1);
};



Figures.composedIsometricMatrix22 = function( figures ) {
  const size = figures.length;
  if( size === 0 ) {
    return new Matrix();
  }
  let rotationSum = 0;
  let scaleSum = 0;
  //_.forEach(figures,function(f) {
  for(let k=0;k<size;++k) {
    const m = figures[k]._matrix22;
    rotationSum += m.determinant() > 0 ? m.angle() : 0;
    scaleSum    += m.scaleFactor();
  }//);
  const    scaleMean =    scaleSum / size;
  const rotationMean = rotationSum / size;
  return Matrix.m( Matrix.scale(scaleMean,scaleMean), Matrix.rotate(rotationMean) );
};

Figures.meanPin = function( figures ) {
  const pinSum = new Point(0,0);
  //_.forEach(figures,function(f) {
  for(let k=0,kEnd=figures.length;k<kEnd;++k) {
    pinSum.add( figures[k].pin ) ;
  }//);
  pinSum.scalarMultiply(1.0/figures.length);
  return pinSum;
};

Figures.getBrushes = function( figures ) {
  return flatten( invokeMap( figures, 'getBrushes' ) );
};

Figures.uniqueBrushZones = function( figures, options, zones = [] ) {
  return reduce(figures, (zones,f) => f.uniqueBrushZones(options,zones), zones );
};

Figures.changeBrushZone = function( figures, zone, newValue ) {
  //.forEach(figures,function(f) {
  for(let k=0,kEnd=figures.length;k<kEnd;++k) {
    figures[k].changeBrushZone( zone, newValue );
  }//);
  return figures;
};

Figures.labelBrushZone = function( figures, zone, label ) {
  const targetBrush = zone.value.textualize();
  forEach( Figures.getBrushes( figures ), brush => {
    if( targetBrush === brush.textualize() ) {
      brush._label = label;
    }
  });
  return figures;
};

Figures.uniqueColorZones = Color.zones;

Figures.changeColorZone = Color.changeZone;

Figures.tagColorZone = function( figures,zone,tag) {
  if( ! ( zone.value instanceof Color ) ) {
    return;
  }
  const color = zone.value.clone();
  color.tag = tag;
  //_.forEach(figures,function(f) {
  for(let k=0,kEnd=figures.length;k<kEnd;++k) {
    figures[k].replaceColor( zone.value, color,
                           { tagsCollapsed: zone.tagsCollapsed,
                             keepTags: false,
                             keepLabels: false });
  }//);
  return figures;
};

Figures.setTaggedColors = function( figures, tag, color ) {
  //_.forEach(figures,function(f) {
  for(let k=0,kEnd=figures.length;k<kEnd;++k) {
    figures[k].setTaggedColors(tag,color);
  }//);
  return figures;
};

Figures.labelColorZone = function( figures, zone, label ) {
  if( ! ( zone.value instanceof Color ) ) {
    return;
  }
  const color = zone.value.clone();
  color.label = label;
  //_.forEach(figures,function(f) {
  for(let k=0,kEnd=figures.length;k<kEnd;++k) {
    figures[k].replaceColor( zone.value, color,
                           { tagsCollapsed: zone.tagsCollapsed,
                             keepTags: true,
                             keepLabels: false });
  }//);
  return figures;
};

Figures.setLabeledColors = function( figures, label, color ) {
  //_.forEach(figures,function(f) {
  for(let k=0,kEnd=figures.length;k<kEnd;++k) {
    figures[k].setLabeledColors(label,color);
  }//);
  return figures;
};

Figures.forEachColor = function( figures, fun, scope ) {
  //_.forEach(figures,function(f) {
  //  _.forEach(f.getColors(),fun,scope);
  //});
  for(let k=0,kEnd=figures.length;k<kEnd;++k) {
    const colors = figures[k].getColors();
    for(let i=0,iEnd=colors.length;i<iEnd;++i) {
      fun.call(scope,colors[i]);
    }
  }
};


Figures.getByAttribute = function(figures, n, v)
{
  var f = [];
  for(let k=0,kEnd=figures.length;k<kEnd;++k) {
    const m = figures[k].metadata.get(n);
    if ( m !== undefined && m === v )
      f.push(figures[k]);
  }
  return f;
};

// [TODO] Simplify! We may not need this anymore now that we
//        have local rendering of most of CDL
Figures.convertColors = function( figures, map, options ) {
  const collapseTags = ( options && options.collapseTags !== undefined ) ? options.collapseTags : false;
  const includeTag = ! collapseTags;
  const keepTags   = ( options && options.keepTags   !== undefined ) ? options.keepTags   : true;
  const keepLabels = ( options && options.keepLabels !== undefined ) ? options.keepLabels : true;
  const zones = [];
  const colorsMap = {};


  const invalidateGroups = function( figures ) {
    for(let i=0,iEnd=figures.length;i<iEnd;++i) {
      const lFigure = ( figures[i] );
      if (lFigure.isGroup)
      {
          lFigure.invalidate();
          invalidateGroups(lFigure._figures);
      }
    }
  }
  invalidateGroups(figures);


  Figures.forEachColor( figures, c => {
    const cs = c.textualize(includeTag);
    if( ! ( cs in colorsMap ) ) {
      colorsMap[cs] = cs ;
      zones.push( { value:c.clone() } ) ;
    }
  });

  const changesMap = {};
  //_.forEach( zones, function(zone,i) {
  for(let k=0,kEnd=zones.length;k<kEnd;++k) {
    const zone = zones[k];
    const newColor = map(zone.value,k);
    if( newColor && newColor !== zone.value ) {
      // to process the output colors
      changesMap[ zone.value.textualize(includeTag) ] = newColor.clone();
    }
  }//);

  const processFigure = function( f ) {

    // If there is an output figure, we also obtain its colors
    // to optimize the invalidation
    // [TODO] This will not work for Effect3D because new colors are created
    //        We need to add further logic here

    if(f._processor instanceof GroupProcessor) {

      //Make sure to update colors on power clip figures that are on the group
      if( f._powerClipContents.length > 0 ) {
        const patternFigures = f._powerClipContents[0]._figures;
        //_.forEach(patternFigures, processFigure);
        for(let k=0,kEnd=patternFigures.length;k<kEnd;++k) {
          processFigure( patternFigures[k] );
        }
      }
      const groupedFigures = f._figures;
      //_.forEach(groupedFigures, processFigure);
      for(let i=0,iEnd=groupedFigures.length;i<iEnd;++i) {
        processFigure( groupedFigures[i] );
      }
    }
    else {

      let changed = false;

      const processColor = function(c) {
        const cs = c.textualize(includeTag) ;
        if(cs in changesMap) {
          c._replace(changesMap[cs],keepTags,keepLabels); // Avoid figure invalidation
          changed = true;
        }
      };

      forEach(f.getColors(),processColor);

      const of = f._outputFigure;
      if( of && f !== of ) {
        forEach(of.getColors(),processColor);
      }

      if( changed ) {

        if( f._powerClipContents.length > 0 ) {
          const pFigures = f._powerClipContents[0]._figures;
          for(let k=0,kEnd=pFigures.length;k<kEnd;++k) {
            const figure = pFigures[k];
            figure.cacheId = null;
            figure.invalidate();
          }
        }

        if( Config.replaceColorsLocallyWhenPossible &&
            Config.computeFigureGeometryLocally     &&
            f.supportsLocalMetrics()                && // We no longer have the cache id
            f.representationType() === 'local' )       // so we need this optimization enabled
        {
          // We are touching the processed figure directly, we can no longer
          // use the stored cacheId figure in the server
          f.cacheId = null;
          f.lock();
          f.invalidateView();
         }
         else {
          f.invalidate();
         }
      }
    }
  };
  forEach(figures,processFigure);
  return figures;
};

Figures.align$ = function( figures, alignment, to, margin ) {
  const toBounds = to && ( to.bounds || ( to.lx !== undefined ? to : null ) );
  if( toBounds ) {
    return map$( figures, f => f.align$(alignment,toBounds,margin) ) .then( () => figures );
  }
  else{
    return Figures.bounds$( to || figures ).then( toBounds => {
      return map$( figures, f => f.align$(alignment,toBounds,margin) ) .then( () => figures );
    });
  }
};

const _groupedAlign$ = function( figures, alignment, toBounds, margin ) {
  return Figures.bounds$(figures) .then( bounds => {
  return Figures.translate(figures, Bounds.alignOffset(bounds,alignment,toBounds,margin) );
  });
};

Figures.groupedAlign$ = function( figures, alignment, to, margin ) {
  const toBounds = to.bounds || ( to.lx !== undefined ? to : null );
  if( toBounds ) {
    return _groupedAlign$(figures,alignment,toBounds,margin);
  }
  else{
    return Figures.bounds$( to ).then( toBounds => {
    return _groupedAlign$(figures,alignment,toBounds,margin);
    });
  }
};

Figures.preparedToDraw = function( figures, zoom ) {
  return every( figures, f => f.preparedToDraw(zoom) );
};
Figures.prepareToDraw$ = function( figures, zoom ) {
  return invokeMap$(figures,'prepareToDraw$',zoom);
};

Figures.filterProcessed = function( figures ) {
  const r = [];
  for( let k = 0, kEnd = figures.length; k < kEnd; ++k ) {
    const figure = figures[k];
    if( figure.processed() ) {
      r.push(figure);
    }
  }
  return r;
};

Figures.forEachText = function( figures, f ) {
  return forEach( filter(figures,'isText'), f );
};

Figures.forEachShape = function( figures, f ) {
  return forEach( filter(figures,'isShape'), f );
};

Figures.forEachRaster = function( figures, f ) {
  return forEach( filter(figures,'isRaster'), f );
};

Figures.forEachGroup = function( figures, f ) {
  return forEach( filter(figures,'isGroup'), f );
};

Figures.forEachProxyGroup = function( figures, f ) {
  return forEach( filter(figures,'isProxyGroup'), f );
};


Figures.deepForEachFigure = function( figures, func, context ) {
  if( isArray(figures) ) {
    for(let k=0,kEnd=figures.length;k<kEnd;++k) {
      if( figures[k].deepForEachFigure(func,context) === false ) {
        return false;
      }
    }
  }
  else {
    return figures.deepForEachFigure(func,context);
  }
};

Figures.figureAtPoint_ = function( fromFigures, point, options = {} ) {

  // Frame selection (Frame selection fallback)
  const figures = [];
  forEachRight( fromFigures, f => {
    if( f.canBeSelected() && f.frame_().contains(point) ) { // [TODO] Use 'selectable'?
      figures.push(f);
    }
  });

  // Shortcircuit, avoid expensive curves based selection if not needed
  if( figures.length === 0 ) {
    return null;       // Nothing around
  }
  if( figures.length === 1 ) {
    return figures[0]; // Only one figure
  }

  // direct selection of figure?
  if( Config.useCurvesBasedSelection ) {
    const figure = find( figures, f => f.hitTest(point,options) );
    if( figure ) {
      return figure;
    }
  }

  return figures[0];
};

/*
const canvas = document.createElement('canvas');
canvas.width  = 512;
canvas.height = 512;

Cx.drawFiguresThumbnail$( canvas, figures ) .then(function(){

log( canvas.toDataURL() );

});
*/

Figures.drawFiguresThumbnail$ = function( canvas, figures ) {

  const { width, height } = canvas;

  // This already process all the figures
  return Figures.bounds$(figures) .then( bounds => {

  const bounds_width = bounds.width, bounds_height = bounds.height;
  const scaleX = width  / bounds_width;
  const scaleY = height / bounds_height;
  const scale = Math.min(scaleX,scaleY);

  const transform = Matrix.concat( Matrix.translate( (width-bounds_width*scale)/2, (height-bounds_height*scale)/2 ),
                                   Matrix.scale(scale,-scale),
                                   Matrix.translate(-bounds.lx,-bounds.hy) );

  const drawConfig = {
    transformDoc: transform
  };

  const context = canvas.getContext('2d');

  const zoom = scale;
  return invokeMap$(figures,'prepareToDraw$',zoom) .then( () => {

  forEach( figures, figure => {
    figure.draw(context,transform,drawConfig);
  });

  return canvas;

  }); });
};

Figures.setUnit$ = function(figures,fromUnit,toUnit) {
  const scale = Figures.getScaleForUnitChange(fromUnit, toUnit)
  return Figures.bounds$(figures).then(function(bounds) {
    Figures.translate(figures, -bounds.center.x, -bounds.center.y)
    return Figures.scale$(figures, scale, scale).then(function() {
      Figures.translate(figures, bounds.center.x*scale, bounds.center.y*scale)
    });
  });
}

Figures.getScaleForUnitChange = function(oldUnit, newUnit) {
  if(oldUnit == 'mm') {
    if(newUnit == 'cm') return 1/10
    if(newUnit == 'in') return 1/25.4
  }

  if(oldUnit == 'in') {
    if(newUnit == 'cm') return 2.54
    if(newUnit == 'mm') return 25.4
  }

  if(oldUnit == 'cm') {
    if(newUnit == 'mm') return 10
    if(newUnit == 'in') return 1/2.54
  }

  return 1
}


/** WEBPACK FOOTER **
 ** ./src/cdl/figure/figures.js
 **/